import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

import { withScheduleContext } from '@mopla/business-logic';
import { useLogger } from '@mopla/logger';

import { createDriverAlertsChannel } from '../config/notificationChannel';
import { environment } from '../environments/environment';
import ViewEntrypoint from '../view/ViewEntrypoint';

import { withDBObservableDataContext } from './withDBObservableDataContext';

export const App = withDBObservableDataContext(
	withScheduleContext(() => {
		const app = getApp();
		const auth = getAuth(app);
		const { t } = useTranslation(['permissions', 'notifications']);
		const logger = useLogger('NotificationChannel');

		useEffect(() => {
			const createChannel = async () => {
				try {
					await createDriverAlertsChannel(
						t('notifications:channel.driver.alerts')
					);
				} catch (err) {
					logger.error('Failed to create the driver alerts channel', {
						error: err,
					});
				}
			};
			createChannel();
		}, [logger, t]);

		useEffect(() => {
			// initialization needed for iOS
			// TODO: maybe switch to native auth for better persistence
			if (app) {
				auth.onIdTokenChanged((user) => {
					if (user && Capacitor.getPlatform() !== 'web') {
						const updateHeader = async () => {
							const token = await user.getIdToken();
							BackgroundGeolocation.setConfig({
								headers: {
									Authorization: 'Bearer ' + token,
								},
							});
						};
						updateHeader();
					}
				});

				auth.onAuthStateChanged((user) => {
					if (user && Capacitor.getPlatform() !== 'web') {
						const ready = async () => {
							const token = await user.getIdToken();
							LocalNotifications.removeAllDeliveredNotifications();
							const state = await BackgroundGeolocation.ready({
								locationAuthorizationRequest: 'Always',
								// Geolocation Config
								desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
								disableLocationAuthorizationAlert: true,
								distanceFilter: 10,
								// Activity Recognition
								preventSuspend: true,
								heartbeatInterval: 60,
								stopTimeout: 1,
								// Application config
								debug: false,
								logLevel:
									environment.env === 'production'
										? BackgroundGeolocation.LOG_LEVEL_WARNING
										: BackgroundGeolocation.LOG_LEVEL_VERBOSE,
								stopOnTerminate: false, // <-- Allow the background-service to continue tracking when app terminated.
								startOnBoot: false, // <-- Auto start tracking when device is powered-up.
								enableHeadless: true,
								// HTTP / SQLite config
								url: environment.baseUrl + '/api/drivers/location',
								locationTemplate:
									'{"accuracy": "<%= accuracy %>", "captureTime": "<%= timestamp %>", "latitude": "<%= latitude %>", "longitude": "<%= longitude %>"}',
								/**
								 * Geofencing was removed, so this should never be rendered.  It is kept as a
								 * fail-safe: without it a stray geofence event falls back to the SDK's default
								 * schema, which /drivers/location cannot parse -- and a rejected record blocks
								 * the whole upload queue for that driver.
								 */
								geofenceTemplate:
									'{"accuracy": "<%= accuracy %>", "captureTime": "<%= timestamp %>", "latitude": "<%= latitude %>", "longitude": "<%= longitude %>"}',
								httpRootProperty: '.',
								disableProviderChangeRecord: true,
								batchSync: false, // <-- Set true to sync locations to server in a single HTTP request.
								autoSync: true, // <-- Set true to sync each location to server as it arrives.
								backgroundPermissionRationale: {
									title: t(
										'permissions:driver_location_always.android_rationale.title'
									),
									message: t(
										'permissions:driver_location_always.android_rationale.description'
									),
									negativeAction: t(
										'permissions:driver_location_always.android_rationale.negative_action'
									),
									positiveAction: t(
										'permissions:driver_location_always.android_rationale.positive_action'
									),
								},
								headers: {
									Authorization: 'Bearer ' + token,
								},
							});
							if (!state.enabled) {
								await BackgroundGeolocation.start();
							}
							/**
							 * Geofencing has been removed from the driver app.  Fences registered by
							 * an earlier version survive an app update (stopOnTerminate: false), so
							 * drop any that are still registered on this device.
							 */
							try {
								await BackgroundGeolocation.removeGeofences();
							} catch (err) {
								console.error('[removeGeofences] failed to clean up', err);
							}
							BackgroundGeolocation.onHeartbeat((event) => {
								console.log('[onHeartbeat] ', event);
								BackgroundGeolocation.getCurrentPosition({
									samples: 1,
									persist: true,
								});
							});

							BackgroundGeolocation.onLocation((location) => {
								console.log('[onLocation', location);
							});

							BackgroundGeolocation.onHttp((response) => {
								console.log('[onHttp] ', response);
							});
						};
						ready();
					}
				});
			}
		}, [app, auth, t]);

		useEffect(() => {
			let listener: PluginListenerHandle;
			const registerCallbacks = async () => {
				listener = await CapacitorApp.addListener('resume', () => {
					/**
					 * when the app is focused again lets clean up everything that we reported to the user
					 * currently this would be schedule update notifications
					 * **/
					LocalNotifications.removeAllDeliveredNotifications();
				});
			};
			registerCallbacks();

			return () => {
				listener?.remove();
			};
		}, []);

		return <ViewEntrypoint />;
	})
);
